
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { findBlockById } from '@/lib/email-builder/block-tree-utils';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { registerPendingEdit } from '@/lib/email-builder/pending-edits';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HexColorPicker } from 'react-colorful';
import { debounce } from '@/lib/utils/debounce';
import type { TypographyConfiguration, AlignmentConfiguration } from '@/types/block-configurations';
import { MergeVariableModal } from '@/components/email-builder/MergeVariableModal';
import { toMergeTag } from '@/lib/email-builder/merge-variables';

/**
 * FormattingToolbar Component
 * 
 * A dynamic, context-aware formatting toolbar positioned at the top of the canvas,
 * inspired by Google Docs. Provides quick access to text formatting options for
 * the currently selected block.
 * 
 * ## Features:
 * - **Font Controls**: Font family selection (email-safe fonts) and size (8-72px)
 * - **Text Styling**: Bold, Italic, Underline, Strikethrough
 * - **Text Color**: Color picker with hex input
 * - **Alignment**: Left, Center, Right, Justify
 * - **Context-Aware**: Only shows relevant controls based on selected block type
 * - **Real-time Updates**: Changes apply instantly to the canvas (optimistic updates)
 * - **Extensible Architecture**: Easy to add new formatting options
 * 
 * ## Supported Block Types:
 * - `text`: Full formatting support (all controls)
 * - `heading`: Typography + alignment (no strikethrough)
 * - `button`: Limited typography + alignment
 * - `quote`: Limited typography + alignment
 * 
 * ## Technical Details:
 * - Uses `updateBlockConfigLocal` for instant canvas updates
 * - Stores text styles (italic, underline, strikethrough) in `textStyle` config property
 * - Typography stored in standard `typography` config property
 * - Alignment stored in standard `alignment` config property
 * 
 * ## Extending the Toolbar:
 * 
 * To add a new formatting option:
 * 
 * 1. Add control to `BLOCK_TOOLBAR_SUPPORT`:
 * ```typescript
 * text: [
 *   { type: 'typography', controls: [..., 'superscript'] }
 * ]
 * ```
 * 
 * 2. Add the button in the toolbar render:
 * ```typescript
 * {typographySection?.controls.includes('superscript') && (
 *   <Button onClick={handleSuperscript}>
 *     <SuperscriptIcon />
 *   </Button>
 * )}
 * ```
 * 
 * 3. Add handler function:
 * ```typescript
 * const handleSuperscript = () => {
 *   // Update config with new property
 * };
 * ```
 * 
 * ## Future Enhancements:
 * - Keyboard shortcuts (Ctrl+B, Ctrl+I, etc.)
 * - Font size input field (in addition to dropdown)
 * - More alignment options (vertical alignment)
 * - List formatting (ordered/unordered)
 * - Link insertion/editing
 * - Background color control
 * - Text highlighting
 * 
 * @see /src/store/emailBuilderStore.ts - For updateBlockConfigLocal
 * @see /src/types/block-configurations.ts - For configuration types
 */

interface ToolbarSection {
  type: 'typography' | 'alignment';
  controls: string[];
}

// Map block types to their supported toolbar sections
const BLOCK_TOOLBAR_SUPPORT: Record<string, ToolbarSection[]> = {
  text: [
    { type: 'typography', controls: ['font', 'size', 'bold', 'italic', 'underline', 'strikethrough', 'color'] },
    { type: 'alignment', controls: ['left', 'center', 'right', 'justify'] }
  ],
  heading: [
    { type: 'typography', controls: ['font', 'size', 'bold', 'italic', 'underline', 'color'] },
    { type: 'alignment', controls: ['left', 'center', 'right'] }
  ],
  button: [
    { type: 'typography', controls: ['font', 'size', 'bold', 'italic', 'color'] },
    { type: 'alignment', controls: ['left', 'center', 'right'] }
  ],
  quote: [
    { type: 'typography', controls: ['font', 'size', 'italic', 'color'] },
    { type: 'alignment', controls: ['left', 'center', 'right'] }
  ],
};

const FONT_FAMILIES = [
  'Arial',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
  'Courier New'
] as const;

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72];

export function FormattingToolbar() {
  const { selectedBlockId, blockTree, updateBlockConfigLocal, persistBlockConfig } = useEmailBuilderStore();
  
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showMergeVarModal, setShowMergeVarModal] = useState(false);

  // Find the selected block
  const selectedBlock = useMemo(() => {
    if (!selectedBlockId) return null;
    return findBlockById(blockTree, selectedBlockId);
  }, [selectedBlockId, blockTree]);

  // Parse block configuration
  const blockConfig = useMemo(() => {
    if (!selectedBlock) return null;
    try {
      return typeof selectedBlock.configuration === 'string'
        ? JSON.parse(selectedBlock.configuration)
        : selectedBlock.configuration || null;
    } catch (e) {
      console.error('Failed to parse block configuration:', e);
      return null;
    }
  }, [selectedBlock]);

  // Get toolbar support for this block type
  const toolbarSupport = useMemo(() => {
    if (!selectedBlock) return null;
    return BLOCK_TOOLBAR_SUPPORT[selectedBlock.blockType] || null;
  }, [selectedBlock]);

  // Debounced API sync (500ms delay) - sends the block's latest local configuration
  const debouncedPersist = useMemo(
    () =>
      debounce((blockId: string) => {
        persistBlockConfig(blockId);
      }, 500),
    [persistBlockConfig]
  );

  // The debouncer keeps only the latest call, so a change to another block must flush the
  // pending one first or the earlier block's change is never sent
  const pendingBlockIdRef = useRef<string | null>(null);
  const debouncedApiSync = useCallback(
    (blockId: string) => {
      if (pendingBlockIdRef.current && pendingBlockIdRef.current !== blockId) {
        debouncedPersist.flush();
      }
      pendingBlockIdRef.current = blockId;
      debouncedPersist(blockId);
    },
    [debouncedPersist]
  );

  // Send pending changes on save/checkpoint (via the registry) and when the toolbar unmounts
  useEffect(() => {
    const flush = () => debouncedPersist.flush();
    const unregister = registerPendingEdit(flush);
    return () => {
      flush();
      unregister();
    };
  }, [debouncedPersist]);

  // Update handlers
  const updateTypography = useCallback((updates: Partial<TypographyConfiguration>) => {
    if (!selectedBlockId || !blockConfig) return;

    const newConfig = {
      ...blockConfig,
      typography: {
        ...blockConfig.typography,
        ...updates,
      }
    };
    
    // 1. Update local state immediately (instant canvas update)
    updateBlockConfigLocal(selectedBlockId, newConfig);
    // 1. Update local state immediately (instant canvas update)
    updateBlockConfigLocal(selectedBlockId, newConfig);
    
    // 2. Debounce the API call (sync to server after 500ms)
    debouncedApiSync(selectedBlockId);
  }, [selectedBlockId, blockConfig, updateBlockConfigLocal, debouncedApiSync]);

  const updateAlignment = useCallback((horizontal: AlignmentConfiguration['horizontal']) => {
    if (!selectedBlockId || !blockConfig) return;

    const newConfig = {
      ...blockConfig,
      alignment: {
        ...blockConfig.alignment,
        horizontal,
      }
    };
    
    // 1. Update local state immediately (instant canvas update)
    updateBlockConfigLocal(selectedBlockId, newConfig);
    
    // 2. Debounce the API call (sync to server after 500ms)
    debouncedApiSync(selectedBlockId);
  }, [selectedBlockId, blockConfig, updateBlockConfigLocal, debouncedApiSync]);

  const toggleFontWeight = useCallback(() => {
    const currentWeight = blockConfig?.typography?.fontWeight || 'normal';
    const newWeight = currentWeight === 'bold' ? 'normal' : 'bold';
    updateTypography({ fontWeight: newWeight });
  }, [blockConfig, updateTypography]);

  const toggleTextStyle = useCallback((style: 'italic' | 'underline' | 'strikethrough') => {
    const current = blockConfig?.typography?.[style] || false;
    updateTypography({ [style]: !current });
  }, [blockConfig, updateTypography]);

  const getTextStyle = useCallback((style: 'italic' | 'underline' | 'strikethrough'): boolean => {
    return blockConfig?.typography?.[style] || false;
  }, [blockConfig]);

  // ---- Merge Variable handlers ----
  const handleOpenMergeVarModal = useCallback(() => {
    setShowMergeVarModal(true);
  }, []);

  const handleInsertMergeVar = useCallback((variable: string, fallback?: string) => {
    if (!selectedBlockId || !blockConfig) return;

    // Build the storage-format span: <span class="merge-variable-placeholder">{{...}}</span>
    const mergeTag = toMergeTag(variable, fallback);
    const storageSpan = `<span class="merge-variable-placeholder">${mergeTag}</span>`;

    // Append the merge variable to the end of the current text content
    const currentText = blockConfig.text || '';
    const newText = currentText + storageSpan;

    const newConfig = { ...blockConfig, text: newText };

    // Update local state immediately (canvas re-renders via toDisplayHtml in TextBlock)
    updateBlockConfigLocal(selectedBlockId, newConfig);
    // Persist to API
    debouncedApiSync(selectedBlockId);
  }, [selectedBlockId, blockConfig, updateBlockConfigLocal, debouncedApiSync]);

  const hasContent = !!(selectedBlockId && selectedBlock && toolbarSupport && blockConfig);
  const typography = blockConfig?.typography as TypographyConfiguration;
  const alignment = blockConfig?.alignment as AlignmentConfiguration;
  const hasTypography = hasContent && toolbarSupport!.some(s => s.type === 'typography');
  const hasAlignment = hasContent && toolbarSupport!.some(s => s.type === 'alignment');
  const typographySection = hasContent ? toolbarSupport!.find(s => s.type === 'typography') : undefined;

  return (
    <div className="lw-formatting-toolbar h-12 border-b border-border bg-card flex items-center justify-center px-4 gap-2">
      {!hasContent && <span />}
      {hasContent && <>
      {/* Font Family */}
      {hasTypography && typographySection?.controls.includes('font') && (
        <div className="flex items-center gap-1">
          <Select
            value={typography?.fontFamily || 'Arial'}
            onChange={(e) => updateTypography({ fontFamily: e.target.value as TypographyConfiguration['fontFamily'] })}
            className="h-8 text-xs w-[140px]"
          >
            {FONT_FAMILIES.map(font => (
              <option key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* Font Size */}
      {hasTypography && typographySection?.controls.includes('size') && (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const currentSize = typography?.fontSize || 14;
              const currentIndex = FONT_SIZES.indexOf(currentSize);
              if (currentIndex > 0) {
                updateTypography({ fontSize: FONT_SIZES[currentIndex - 1] });
              }
            }}
            className="h-8 w-7 p-0"
            title="Decrease font size"
            disabled={!typography?.fontSize || FONT_SIZES.indexOf(typography.fontSize) <= 0}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </Button>
          <Select
            value={typography?.fontSize?.toString() || '14'}
            onChange={(e) => updateTypography({ fontSize: parseInt(e.target.value) })}
            className="h-8 text-xs w-[70px]"
          >
            {FONT_SIZES.map(size => (
              <option key={size} value={size.toString()}>
                {size}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const currentSize = typography?.fontSize || 14;
              const currentIndex = FONT_SIZES.indexOf(currentSize);
              if (currentIndex >= 0 && currentIndex < FONT_SIZES.length - 1) {
                updateTypography({ fontSize: FONT_SIZES[currentIndex + 1] });
              }
            }}
            className="h-8 w-7 p-0"
            title="Increase font size"
            disabled={!typography?.fontSize || FONT_SIZES.indexOf(typography.fontSize) >= FONT_SIZES.length - 1}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </Button>
        </div>
      )}

      {/* Divider */}
      {hasTypography && (
        <div className="w-px h-6 bg-border" />
      )}

      {/* Bold */}
      {hasTypography && typographySection?.controls.includes('bold') && (
        <Button
          type="button"
          size="sm"
          variant={typography?.fontWeight === 'bold' ? 'default' : 'outline'}
          onClick={toggleFontWeight}
          className="h-8 w-8 p-0"
          title="Bold (Ctrl+B)"
        >
          <BoldIcon />
        </Button>
      )}

      {/* Italic */}
      {hasTypography && typographySection?.controls.includes('italic') && (
        <Button
          type="button"
          size="sm"
          variant={getTextStyle('italic') ? 'default' : 'outline'}
          onClick={() => toggleTextStyle('italic')}
          className="h-8 w-8 p-0"
          title="Italic (Ctrl+I)"
        >
          <ItalicIcon />
        </Button>
      )}

      {/* Underline */}
      {hasTypography && typographySection?.controls.includes('underline') && (
        <Button
          type="button"
          size="sm"
          variant={getTextStyle('underline') ? 'default' : 'outline'}
          onClick={() => toggleTextStyle('underline')}
          className="h-8 w-8 p-0"
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon />
        </Button>
      )}

      {/* Strikethrough */}
      {hasTypography && typographySection?.controls.includes('strikethrough') && (
        <Button
          type="button"
          size="sm"
          variant={getTextStyle('strikethrough') ? 'default' : 'outline'}
          onClick={() => toggleTextStyle('strikethrough')}
          className="h-8 w-8 p-0"
          title="Strikethrough"
        >
          <StrikethroughIcon />
        </Button>
      )}

      {/* Divider */}
      {hasTypography && hasAlignment && (
        <div className="w-px h-6 bg-border" />
      )}

      {/* Text Color */}
      {hasTypography && typographySection?.controls.includes('color') && (
        <Popover open={showColorPicker} onOpenChange={setShowColorPicker}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 relative"
              title="Text Color"
            >
              <TextColorIcon />
              <div 
                className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-1.5 rounded border border-border"
                style={{ backgroundColor: typography?.color || '#000000' }}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="center">
            <div className="space-y-3">
              <label className="text-xs font-medium block">Text Color</label>
              <HexColorPicker 
                color={typography?.color || '#000000'} 
                onChange={(color) => updateTypography({ color })} 
              />
              <input
                type="text"
                value={typography?.color || '#000000'}
                onChange={(e) => {
                  // Validate hex color
                  const value = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                    updateTypography({ color: value });
                  }
                }}
                className="w-full h-9 px-2 text-xs border border-border rounded font-mono"
                placeholder="#000000"
                maxLength={7}
              />
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Divider */}
      {hasAlignment && (
        <div className="w-px h-6 bg-border" />
      )}

      {/* Alignment Controls */}
      {hasAlignment && (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant={alignment?.horizontal === 'left' ? 'default' : 'outline'}
            onClick={() => updateAlignment('left')}
            className="h-8 w-8 p-0"
            title="Align Left"
          >
            <AlignLeftIcon />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={alignment?.horizontal === 'center' ? 'default' : 'outline'}
            onClick={() => updateAlignment('center')}
            className="h-8 w-8 p-0"
            title="Align Center"
          >
            <AlignCenterIcon />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={alignment?.horizontal === 'right' ? 'default' : 'outline'}
            onClick={() => updateAlignment('right')}
            className="h-8 w-8 p-0"
            title="Align Right"
          >
            <AlignRightIcon />
          </Button>
          {typographySection?.controls.includes('justify') && (
            <Button
              type="button"
              size="sm"
              variant={alignment?.horizontal === 'justify' ? 'default' : 'outline'}
              onClick={() => updateAlignment('justify')}
              className="h-8 w-8 p-0"
              title="Justify"
            >
              <AlignJustifyIcon />
            </Button>
          )}
        </div>
      )}

      {/* Merge Variable (text blocks only) */}
      {selectedBlock?.blockType === 'text' && (
        <>
          <div className="w-px h-6 bg-border" />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleOpenMergeVarModal}
            className="h-8 px-2 gap-1 text-xs"
            title="Insert Merge Variable"
          >
            <MergeVarIcon />
            <span className="hidden sm:inline">Merge Variable</span>
          </Button>
          <MergeVariableModal
            isOpen={showMergeVarModal}
            onClose={() => setShowMergeVarModal(false)}
            onSave={handleInsertMergeVar}
          />
        </>
      )}
      </>}
    </div>
  );
}

// Icon Components (Inline SVG for simplicity)
function BoldIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
  );
}

function ItalicIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="19" y1="4" x2="10" y2="4" strokeWidth={2} strokeLinecap="round" />
      <line x1="14" y1="20" x2="5" y2="20" strokeWidth={2} strokeLinecap="round" />
      <line x1="15" y1="4" x2="9" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function UnderlineIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3M4 21h16" />
    </svg>
  );
}

function StrikethroughIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M8 5c-1.5 0-2.7.5-3.5 1.3M16 5c1.5 0 2.7.5 3.5 1.3M9 19c1 .7 2.2 1 3.5 1 1.3 0 2.5-.3 3.5-1" />
    </svg>
  );
}

function TextColorIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 18L12 4l7 14" />
      <line x1="8" y1="14" x2="16" y2="14" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignLeftIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="10" x2="14" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="15" x2="18" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="20" x2="11" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignCenterIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="7" y1="10" x2="17" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="5" y1="15" x2="19" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="8" y1="20" x2="16" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignRightIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="10" y1="10" x2="21" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="6" y1="15" x2="21" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="13" y1="20" x2="21" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignJustifyIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="10" x2="21" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="15" x2="21" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="20" x2="21" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function MergeVarIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 4C5.5 4 4 5.5 4 7.5S5 11 7 12c-2 1-3 3-3 4.5S5.5 20 7 20" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 4c1.5 0 3 1.5 3 3.5S19 11 17 12c2 1 3 3 3 4.5S18.5 20 17 20" />
    </svg>
  );
}
